import { useState, useEffect } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdOutlinePayment, MdCheck, MdChevronRight } from 'react-icons/md';

interface AccountStatus {
    hasAccount: boolean;
    detailsSubmitted: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
}

interface Props {
    user: { isKYCVerified: boolean } | null;
    onUserRefresh: () => void;
}

const STEPS = [
    { key: 'hasAccount', label: 'Compte Stripe cree' },
    { key: 'detailsSubmitted', label: 'Informations soumises' },
    { key: 'chargesEnabled', label: 'Paiements actives' },
    { key: 'payoutsEnabled', label: 'Virements actives' },
] as const;

const KYCBanner = ({ user, onUserRefresh }: Props) => {
    const [isURLLoading, setIsURLLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');
    const [status, setStatus] = useState<AccountStatus | null>(null);
    const [isStatusLoading, setIsStatusLoading] = useState(true);

    const authHeaders = () => ({
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
    });

    useEffect(() => {
        setIsStatusLoading(true);
        axios
            .get(`${CONFIG.apiUrl}/stripe/account-status`, authHeaders())
            .then((res) => setStatus(res.data))
            .catch(() => {})
            .finally(() => setIsStatusLoading(false));
    }, [user]);

    const allStepsDone = status !== null && STEPS.every((step) => status[step.key]);
    if (allStepsDone) return null;

    const startKYCProcess = () => {
        setIsURLLoading(true);
        axios
            .get(`${CONFIG.apiUrl}/stripe/kyc`, authHeaders())
            .then((res) => { window.location.href = res.data.url; })
            .catch((err) => { console.log('Impossible to prepare KYC process', err); setIsURLLoading(false); });
    };

    const syncStripeAccount = () => {
        setIsSyncing(true);
        setSyncMessage('');
        axios
            .get(`${CONFIG.apiUrl}/stripe/sync-account`, authHeaders())
            .then((res) => {
                setSyncMessage(res.data.synced ? 'Compte synchronise avec succes !' : 'Deja synchronise.');
                onUserRefresh();
            })
            .catch(() => setSyncMessage('Aucun compte Stripe trouve.'))
            .finally(() => setIsSyncing(false));
    };

    const isWaitingForStripe = status?.detailsSubmitted && !status?.payoutsEnabled;
    const ctaLabel = isURLLoading ? 'Chargement...' : status?.hasAccount ? "Continuer l'onboarding" : 'Creer mon compte Stripe';

    return (
        <div className="rounded-xl border border-leadrs-stroke bg-white">
            <div className="flex items-center gap-3 px-5 py-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-leadrs-red-soft">
                    <MdOutlinePayment className="h-5 w-5 text-leadrs-red-focus" />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <p className="font-geist text-sm font-semibold text-leadrs-text">
                            Activez votre compte vendeur
                        </p>
                        <span className="rounded-md bg-leadrs-red-soft px-2 py-0.5 font-geist text-[10px] font-semibold uppercase tracking-wide text-leadrs-red-focus">
                            Important
                        </span>
                    </div>
                    <p className="font-geist text-xs text-leadrs-text-soft">
                        Connectez Stripe pour recevoir vos paiements.
                    </p>
                </div>
            </div>

            <hr className="border-leadrs-stroke" />

            <div className="px-5 py-4">
                {isStatusLoading ? (
                    <div className="flex items-center gap-2">
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-leadrs-stroke border-t-leadrs-brand" />
                        <span className="font-geist text-xs text-leadrs-text-soft">Verification du statut...</span>
                    </div>
                ) : (
                    <ol className="flex flex-wrap items-center gap-2">
                        {STEPS.map((step, i) => {
                            const done = status?.[step.key] ?? false;
                            const isNext = !done && (i === 0 || (status?.[STEPS[i - 1].key] ?? false));
                            return (
                                <li key={step.key} className="flex items-center gap-1.5">
                                    <span
                                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-geist text-[10px] font-bold ${
                                            done
                                                ? 'bg-leadrs-green-focus text-white'
                                                : isNext
                                                ? 'bg-leadrs-brand text-white'
                                                : 'border border-leadrs-stroke text-leadrs-text-soft'
                                        }`}
                                    >
                                        {done ? <MdCheck className="h-3 w-3" /> : i + 1}
                                    </span>
                                    <span
                                        className={`font-geist text-xs ${
                                            done
                                                ? 'text-leadrs-text-soft line-through'
                                                : isNext
                                                ? 'font-semibold text-leadrs-text'
                                                : 'text-leadrs-text-soft'
                                        }`}
                                    >
                                        {step.label}
                                    </span>
                                    {i < STEPS.length - 1 && (
                                        <MdChevronRight className="h-4 w-4 text-leadrs-text-soft" />
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>

            <hr className="border-leadrs-stroke" />

            <div className="flex flex-wrap items-center gap-3 px-5 py-3">
                {isWaitingForStripe ? (
                    <span className="font-geist text-xs text-leadrs-text-soft">
                        En attente de validation par Stripe
                    </span>
                ) : (
                    <button
                        onClick={startKYCProcess}
                        disabled={isURLLoading}
                        className="rounded-lg bg-leadrs-brand px-4 py-1.5 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:opacity-50"
                    >
                        {ctaLabel}
                    </button>
                )}
                <button
                    onClick={syncStripeAccount}
                    disabled={isSyncing}
                    className="rounded-lg border border-leadrs-stroke px-3 py-1.5 font-geist text-sm font-medium text-leadrs-text transition-colors hover:border-leadrs-brand disabled:opacity-50"
                >
                    {isSyncing ? 'Synchronisation...' : 'Rafraichir'}
                </button>
                {syncMessage && (
                    <span className="font-geist text-xs text-leadrs-text-soft">{syncMessage}</span>
                )}
            </div>
        </div>
    );
};

export default KYCBanner;
